import { useMemo } from "react";
import type { Person } from "../data/types";

/**
 * Reporting-line chart with dynamic Level 0.
 *
 *   Level 0 — Exec who has other Execs reporting to them (e.g. Laura)
 *   Level 1 — Exec (either a root with no Exec reports, or nested under a Lvl 0)
 *   Level 2 — CLT, connected to the Exec named in their CoveredBy
 *   Level 3 — User, connected to the CLT named in their CoveredBy
 *   Level 4+ — User reporting to another User, nested arbitrarily deep
 *
 * Users whose CoveredBy resolves to an Exec rather than a CLT still render
 * at Level 3 (or deeper if the Exec is at Level 0) — passthrough spacer(s)
 * are inserted above them to preserve grid alignment.
 *
 * CLTs whose CoveredBy resolves to another CLT (peer coverage — e.g. one
 * C&W Manager temporarily covering another) are placed as siblings under
 * their transitive Exec ancestor (found by walking the CoveredBy chain
 * through CLTs until hitting an Exec). The connecting stem is rendered as
 * a dashed brass line instead of the standard solid rule, and the card
 * carries a small "covers: <n>" subtitle to surface the direct coverer.
 *
 * People whose CoveredBy can't be resolved to a sensible parent are collected
 * in an "Unassigned" panel so they stay visible without breaking the layout.
 */

function normaliseName(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, " ");
}

interface OrgNode {
  person: Person;
  children: OrgNode[];
  isCoveragePeer?: boolean;
  coveredBySibling?: string;
  passthrough?: boolean;
  visualLevel?: number;
}

interface OrgRoot {
  node: OrgNode;
  level: 0 | 1;
}

interface OrgTree {
  roots: OrgRoot[];
  hasLevel0: boolean;
  orphanCLTs: Person[];
  orphanUsers: Person[];
}

function buildOrgTree(people: Person[]): OrgTree {
  const active = people.filter((p) => p.active);

  /* ----- Execs: build nodes, then nest Execs-covered-by-Execs ----- */

  const execNodes: OrgNode[] = active
    .filter((p) => p.team === "Exec")
    .map((p) => ({ person: p, children: [] }));
  const execByNorm = new Map<string, OrgNode>();
  for (const e of execNodes) execByNorm.set(normaliseName(e.person.fullName), e);

  const execRootNodes: OrgNode[] = [];
  for (const e of execNodes) {
    const parentKey = e.person.coveredBy ? normaliseName(e.person.coveredBy) : null;
    const parent = parentKey ? execByNorm.get(parentKey) : null;
    if (parent && parent !== e) {
      parent.children.push(e);
    } else {
      execRootNodes.push(e);
    }
  }

  const roots: OrgRoot[] = execRootNodes.map((node) => {
    const hasExecChild = node.children.some((c) => c.person.team === "Exec");
    return { node, level: hasExecChild ? 0 : 1 };
  });
  const hasLevel0 = roots.some((r) => r.level === 0);

  const execLevel = new Map<string, number>();
  function assignExecLevel(node: OrgNode, currentLevel: number) {
    if (node.person.team !== "Exec") return;
    execLevel.set(normaliseName(node.person.fullName), currentLevel);
    for (const c of node.children) assignExecLevel(c, currentLevel + 1);
  }
  for (const r of roots) assignExecLevel(r.node, r.level);

  /* ----- CLTs ----- */

  const allCLTs = active.filter((p) => p.team === "CLT");
  const cltPersonByNorm = new Map<string, Person>();
  for (const p of allCLTs) cltPersonByNorm.set(normaliseName(p.fullName), p);

  const cltNodeByPerson = new Map<number, OrgNode>();
  for (const p of allCLTs) {
    cltNodeByPerson.set(p.id, { person: p, children: [] });
  }
  const cltByNorm = new Map<string, OrgNode>();
  for (const node of cltNodeByPerson.values()) {
    cltByNorm.set(normaliseName(node.person.fullName), node);
  }

  function resolveCLTExecAncestor(
    clt: Person,
  ): { exec: OrgNode | null; directCoverer: Person | null } {
    const visited = new Set<number>();
    let current: Person | null = clt;
    let directCoverer: Person | null = null;
    while (current) {
      if (visited.has(current.id)) return { exec: null, directCoverer: null };
      visited.add(current.id);
      const parentKey = current.coveredBy ? normaliseName(current.coveredBy) : null;
      if (!parentKey) return { exec: null, directCoverer: null };
      const parentExec = execByNorm.get(parentKey);
      if (parentExec) return { exec: parentExec, directCoverer };
      const parentCLT = cltPersonByNorm.get(parentKey);
      if (!parentCLT) return { exec: null, directCoverer: null };
      if (!directCoverer) directCoverer = parentCLT;
      current = parentCLT;
    }
    return { exec: null, directCoverer: null };
  }

  const orphanCLTs: Person[] = [];
  for (const p of allCLTs) {
    const node = cltNodeByPerson.get(p.id)!;
    const { exec, directCoverer } = resolveCLTExecAncestor(p);
    if (exec) {
      exec.children.push(node);
      if (directCoverer) {
        node.isCoveragePeer = true;
        node.coveredBySibling = directCoverer.fullName;
      }
    } else {
      orphanCLTs.push(p);
    }
  }

  /* ----- Users (iteratively, handles User→User chains) ----- */

  const userByNorm = new Map<string, OrgNode>();
  let remaining = active.filter((p) => p.team === "User");
  for (let iter = 0; iter < 12; iter++) {
    if (remaining.length === 0) break;
    const next: Person[] = [];
    let placedAny = false;
    for (const p of remaining) {
      const parentKey = p.coveredBy ? normaliseName(p.coveredBy) : null;
      const parentCLT = parentKey ? cltByNorm.get(parentKey) : null;
      const parentExec = parentKey ? execByNorm.get(parentKey) : null;
      const parentUser = parentKey ? userByNorm.get(parentKey) : null;
      const userNode: OrgNode = { person: p, children: [] };
      if (parentCLT) {
        parentCLT.children.push(userNode);
      } else if (parentExec) {
        const parentLvl =
          execLevel.get(normaliseName(p.coveredBy ?? "")) ?? 1;
        const padCount = Math.max(0, 2 - parentLvl);
        let chainTop: OrgNode = userNode;
        for (let i = 0; i < padCount; i++) {
          chainTop = {
            person: p,
            passthrough: true,
            visualLevel: 2 - i,
            children: [chainTop],
          };
        }
        parentExec.children.push(chainTop);
      } else if (parentUser) {
        parentUser.children.push(userNode);
      } else {
        next.push(p);
        continue;
      }
      userByNorm.set(normaliseName(p.fullName), userNode);
      placedAny = true;
    }
    remaining = next;
    if (!placedAny) break;
  }
  const orphanUsers: Person[] = remaining;

  /* ----- Sort all children alphabetically ----- */

  const byName = (a: OrgNode, b: OrgNode) =>
    a.person.fullName.localeCompare(b.person.fullName);
  function sortDeep(node: OrgNode) {
    node.children.sort(byName);
    for (const c of node.children) sortDeep(c);
  }
  roots.sort((a, b) => {
    if (a.level !== b.level) return a.level - b.level;
    return byName(a.node, b.node);
  });
  for (const r of roots) sortDeep(r.node);
  orphanCLTs.sort((a, b) => a.fullName.localeCompare(b.fullName));
  orphanUsers.sort((a, b) => a.fullName.localeCompare(b.fullName));

  return { roots, hasLevel0, orphanCLTs, orphanUsers };
}

/* ---------- presentation ---------- */

const TEAM_PILL: Record<"Exec" | "CLT" | "User", string> = {
  Exec: "bg-[#E8E1FC] text-[#4B3F90]",
  CLT: "bg-[rgb(185_138_58/0.12)] text-brass",
  User: "bg-rag-unset-bg text-ink-3",
};

function NodeCard({ person, level, coversName }: { person: Person; level: number; coversName?: string }) {
  const team = person.team;
  const pillCls = team ? TEAM_PILL[team] : "bg-rag-unset-bg text-ink-3";
  const widthCls =
    level <= 1
      ? "w-[220px]"
      : level === 2
        ? "w-[196px]"
        : "w-[176px]";
  const levelBorder =
    level <= 1
      ? "border-[#4B3F90]/40"
      : level === 2
        ? "border-brass/40"
        : "border-rule-strong";
  return (
    <div
      className={[
        "rounded-md border-2 bg-card px-[12px] py-[10px] text-center shadow-card",
        widthCls,
        levelBorder,
      ].join(" ")}
    >
      <div className="mb-[4px] flex items-center justify-center">
        {team ? (
          <span
            className={`rounded-full px-[7px] py-[1px] text-[9.5px] font-medium ${pillCls}`}
          >
            {team}
          </span>
        ) : null}
      </div>
      <div className="truncate font-display text-[13px] font-medium text-ink">
        {person.fullName}
      </div>
      {person.jobTitle ? (
        <div className="mt-[2px] truncate font-mono text-[9.5px] uppercase tracking-[0.08em] text-ink-3">
          {person.jobTitle}
        </div>
      ) : null}
      {person.department ? (
        <div className="mt-[2px] truncate text-[10.5px] text-ink-2">
          {person.department}
        </div>
      ) : null}
      {coversName ? (
        <div className="mt-[4px] truncate font-mono text-[9px] uppercase tracking-[0.12em] text-brass">
          covered by {coversName}
        </div>
      ) : null}
    </div>
  );
}

function Subtree({ node, level }: { node: OrgNode; level: number }) {
  const hasChildren = node.children.length > 0;
  return (
    <div className="flex flex-col items-center">
      {node.passthrough ? (
        <PassthroughSpacer level={node.visualLevel ?? level} withWire />
      ) : (
        <NodeCard
          person={node.person}
          level={level}
          coversName={node.isCoveragePeer ? node.coveredBySibling : undefined}
        />
      )}
      {hasChildren ? (
        <>
          <div className="h-[18px] w-[2px] bg-rule-strong" />
          <div className="flex items-start">
            {node.children.map((child, i) => {
              const first = i === 0;
              const last = i === node.children.length - 1;
              const single = node.children.length === 1;
              const coverage = !!child.isCoveragePeer;
              return (
                <div
                  key={`${child.person.id}-${i}`}
                  className="relative flex flex-col items-center px-[12px]"
                >
                  {!single ? (
                    <>
                      <div
                        className={[
                          "absolute top-0 left-0 right-1/2 h-[2px]",
                          first ? "" : "bg-rule-strong",
                        ].join(" ")}
                      />
                      <div
                        className={[
                          "absolute top-0 left-1/2 right-0 h-[2px]",
                          last ? "" : "bg-rule-strong",
                        ].join(" ")}
                      />
                    </>
                  ) : null}
                  {coverage ? (
                    <div className="h-[18px] w-0 border-l-2 border-dashed border-brass" />
                  ) : (
                    <div className="h-[18px] w-[2px] bg-rule-strong" />
                  )}
                  <Subtree node={child} level={level + 1} />
                </div>
              );
            })}
          </div>
        </>
      ) : null}
    </div>
  );
}

function RootSubtree({
  root,
  padTop,
}: {
  root: OrgRoot;
  padTop: boolean;
}) {
  return (
    <div className="flex flex-col items-center">
      {padTop ? (
        <>
          <PassthroughSpacer level={0} withWire={false} />
          <div className="h-[18px] w-[2px]" />
        </>
      ) : null}
      <Subtree node={root.node} level={root.level} />
    </div>
  );
}

export function OrgChart({ people }: { people: Person[] }) {
  const tree = useMemo(() => buildOrgTree(people), [people]);

  const totalOnChart = useMemo(() => {
    function count(n: OrgNode): number {
      const self = n.passthrough ? 0 : 1;
      return self + n.children.reduce((s, c) => s + count(c), 0);
    }
    return tree.roots.reduce((s, r) => s + count(r.node), 0);
  }, [tree]);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-x-5 gap-y-2 font-mono text-[10.5px] uppercase tracking-[0.1em] text-ink-2">
        <LegendPill cls={TEAM_PILL.Exec} label="Level 0/1 · Exec" />
        <LegendPill cls={TEAM_PILL.CLT} label="Level 2 · CLT" />
        <LegendPill cls={TEAM_PILL.User} label="Level 3+ · User" />
        <span className="inline-flex items-center gap-[6px]">
          <span className="inline-block h-[14px] w-0 border-l-2 border-dashed border-brass" />
          Peer coverage
        </span>
        <span className="text-ink-3 normal-case tracking-normal">
          {totalOnChart} people · solid stems are direct reports, dashed stems are peer coverage
        </span>
      </div>

      <div className="overflow-x-auto rounded-md border border-rule bg-paper-sunk/30 p-6">
        {tree.roots.length === 0 ? (
          <p className="italic text-ink-3">No active Exec-level people to anchor the chart.</p>
        ) : (
          <div className="flex min-w-fit items-start justify-center gap-[32px]">
            {tree.roots.map((r) => (
              <RootSubtree
                key={r.node.person.id}
                root={r}
                padTop={r.level === 1 && tree.hasLevel0}
              />
            ))}
          </div>
        )}
      </div>

      {tree.orphanCLTs.length > 0 || tree.orphanUsers.length > 0 ? (
        <div className="mt-6 rounded-md border border-rag-amber bg-rag-amber-bg/40 p-4">
          <h3 className="mb-2 font-mono text-[11px] uppercase tracking-[0.14em] text-rag-amber-ink">
            Unassigned · CoveredBy doesn't resolve to the expected level
          </h3>
          {tree.orphanCLTs.length > 0 ? (
            <div className="mb-2 text-[12.5px] text-ink-2">
              <span className="font-medium text-ink">CLT without an Exec leader:</span>{" "}
              {tree.orphanCLTs.map((p) => p.fullName).join(", ")}
            </div>
          ) : null}
          {tree.orphanUsers.length > 0 ? (
            <div className="text-[12.5px] text-ink-2">
              <span className="font-medium text-ink">User without a CLT leader:</span>{" "}
              {tree.orphanUsers.map((p) => p.fullName).join(", ")}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function LegendPill({ cls, label }: { cls: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-[6px]">
      <span className={`inline-block h-[10px] w-[18px] rounded-full ${cls}`} />
      {label}
    </span>
  );
}

function PassthroughSpacer({
  level,
  withWire,
}: {
  level: number;
  withWire: boolean;
}) {
  const ghost: Person = {
    id: -1,
    fullName: "Placeholder Name",
    team: level <= 1 ? "Exec" : level === 2 ? "CLT" : "User",
    department: "People",
    jobTitle: "Placeholder Role",
    active: true,
    coveredBy: null,
  };
  return (
    <div className="relative" aria-hidden="true">
      <div className="invisible">
        <NodeCard person={ghost} level={level} />
      </div>
      {withWire ? (
        <div className="pointer-events-none absolute inset-y-0 left-1/2 w-[2px] -translate-x-1/2 bg-rule-strong" />
      ) : null}
    </div>
  );
}
